import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AnnotatorReports,
  getAnnotatorReports,
  postAnnotatorReport,
} from '../../apis/reports'
import { ReportTarget, reportLabel } from './labels'

const STALE_MS = 30_000
const REPORT_FAILED = '報告できませんでした'

export const reportsQueryKey = (ocrRequestEventId: string) =>
  ['annotator', 'reports', ocrRequestEventId] as const

/**
 * この紙で annotator が立てた旗の読み書き。
 *
 * - 読み: `GET reports/` の labels を Set で持つ（行は label で引く）
 * - 書き: POST の応答（更新後の一覧）でキャッシュを差し替える。楽観更新はしない
 *   （店の旗と同じく 1 往復遅れ。押した直後に戻るより、押せていないことがそのまま見えるほうがよい）
 * - 失敗は行の一言（`errorOn`）。toast にしない（顧客側 useReportField と同じ形）
 *
 * 店の旗（Prescription JSON の is_reported）はここでは持たない。行の塗りは ItemsPane が
 * 「店の旗 or annotator の旗」で合成する。
 */
export const useAnnotatorReport = (ocrRequestEventId: string) => {
  const queryClient = useQueryClient()
  const key = reportsQueryKey(ocrRequestEventId)

  const query = useQuery({
    queryKey: key,
    queryFn: async () => {
      const { data } = await getAnnotatorReports(ocrRequestEventId)
      return data
    },
    staleTime: STALE_MS,
  })

  const mutation = useMutation({
    mutationFn: async (vars: { target: ReportTarget; isReported: boolean }) => {
      const { data } = await postAnnotatorReport(ocrRequestEventId, {
        label: reportLabel(vars.target),
        is_reported: vars.isReported,
      })
      return data
    },
    onSuccess: (data: AnnotatorReports) => queryClient.setQueryData(key, data),
    // 1 往復の間に 2 欄押すと応答の順が入れ替わり古い一覧で上書きし得るので、落ち着いたら 1 回読み直す
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  })

  const labels = new Set(query.data?.labels ?? [])
  const failedLabel =
    mutation.isError && mutation.variables
      ? reportLabel(mutation.variables.target)
      : null

  return {
    /** annotator がこの欄に旗を立てているか */
    isFlagged: (target: ReportTarget) => labels.has(reportLabel(target)),
    setFlag: (target: ReportTarget, isReported: boolean) =>
      mutation.mutate({ target, isReported }),
    /** この行の直近の送信が失敗していれば、行に出す一言 */
    errorOn: (target: ReportTarget) =>
      failedLabel === reportLabel(target) ? REPORT_FAILED : undefined,
  }
}
